import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ExternalLink, Loader2, MessageCircle, QrCode, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { WA_GREEN, formatPhone, waMeUrl } from "@/utils/whatsapp";
import { WaDialog } from "./WaDialog";
import { WhatsAppQRModal } from "./WhatsAppQRModal";
import { useUserWhatsApp } from "./useUserWhatsApp";

export type WaRecipient = { name: string; phone: string; type: "person" | "staff" | "employee" | "other"; id?: string };
type Tpl = { key: string; name: string; body: string };

export function WhatsAppSendModal({ open, onClose, recipient }: { open: boolean; onClose: () => void; recipient: WaRecipient }) {
    const { t, i18n } = useTranslation();
    const { activeMembership } = useAuth();
    const { status } = useUserWhatsApp(open);
    const [tpls, setTpls] = useState<Tpl[]>([]);
    const [tpl, setTpl] = useState("");
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [qrOpen, setQrOpen] = useState(false);

    useEffect(() => {
        if (!open) return;
        setTpl(""); setText("");
        api.get(`/user-whatsapp/templates?lang=${i18n.language.slice(0, 2)}`).then((r) => setTpls(r.data.items || [])).catch(() => setTpls([]));
    }, [open, i18n.language]);

    const fill = (key: string) => {
        setTpl(key);
        const b = tpls.find((x) => x.key === key)?.body;
        if (b) setText(b.replace(/\{\{name\}\}/g, recipient.name).replace(/\{\{institution\}\}/g, activeMembership?.tenant.name || t("uwa.send.institution_fallback")));
    };

    const send = async () => {
        setBusy(true);
        try {
            const r = await api.post("/user-whatsapp/send", { to_phone: recipient.phone, message: text, recipient_name: recipient.name, recipient_type: recipient.type, recipient_id: recipient.id, template_key: tpl || null });
            toast.success(r.data.mocked ? t("uwa.send.sent_mock") : t("uwa.send.sent"));
            onClose();
        } catch (e: any) {
            const d = e?.response?.data?.detail;
            const known = ["whatsapp_not_connected", "recipient_not_on_whatsapp", "whatsapp_gateway_unavailable", "whatsapp_send_failed"];
            toast.error(d?.error === "whatsapp_not_connected" ? t("uwa.send.not_connected") : known.includes(d?.error) ? t(`uwa.err.${d.error}` as any) : formatApiError(e));
        } finally { setBusy(false); }
    };

    const connected = !!status?.connected;
    return (
        <>
            <WaDialog open={open && !qrOpen} onClose={onClose} testid="wa-send-modal" icon={<MessageCircle size={20} />}
                title={t("uwa.send.title")}
                subtitle={<span data-testid="wa-send-recipient"><span className="font-medium" style={{ color: "var(--text)" }}>{recipient.name}</span> · <span className="tabular-nums" data-testid="wa-send-phone">{formatPhone(recipient.phone)}</span></span>}>
                <div className="space-y-4">
                    {status && !connected && (
                        <div className="flex items-start gap-2 rounded-xl p-3 text-xs" style={{ background: "var(--warning-bg)", color: "var(--warning-fg)" }} data-testid="wa-send-not-connected">
                            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                            <div className="flex-1">{t("uwa.send.not_connected")}</div>
                            <button type="button" data-testid="wa-send-connect" onClick={() => setQrOpen(true)} className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 font-medium text-white" style={{ background: WA_GREEN }}><QrCode size={12} />{t("uwa.topbar.connect")}</button>
                        </div>
                    )}
                    {connected && status?.phone && <div className="text-xs" style={{ color: "var(--text-muted)" }} data-testid="wa-send-from">{t("uwa.send.from", { phone: formatPhone(status.phone) })}{status.mocked ? ` · ${t("uwa.mock.badge")}` : ""}</div>}
                    <label className="block">
                        <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("uwa.send.template")}</div>
                        <select data-testid="wa-send-template" value={tpl} onChange={(e) => fill(e.target.value)} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 40 }}>
                            <option value="">{t("uwa.send.template_none")}</option>
                            {tpls.map((x) => <option key={x.key} value={x.key}>{x.name}</option>)}
                        </select>
                    </label>
                    <label className="block">
                        <div className="flex justify-between text-xs mb-1" style={{ color: "var(--text-muted)" }}><span>{t("uwa.send.message")}</span><span className="tabular-nums">{text.length}/4096</span></div>
                        <textarea data-testid="wa-send-text" value={text} maxLength={4096} rows={6} onChange={(e) => setText(e.target.value)} placeholder={t("uwa.send.placeholder")} className="surface-input px-3 py-2 w-full text-sm resize-y" />
                    </label>
                    <div className="flex flex-col-reverse sm:flex-row gap-2 sm:items-center sm:justify-between">
                        <a href={waMeUrl(recipient.phone, text)} target="_blank" rel="noopener noreferrer" data-testid="wa-send-web-link" className="inline-flex items-center justify-center gap-1.5 text-sm font-medium hover:underline" style={{ color: WA_GREEN }}>
                            <ExternalLink size={14} />{t("uwa.send.open_web")}
                        </a>
                        <button type="button" data-testid="wa-send-submit" disabled={busy || !text.trim() || !connected} onClick={send}
                            className="inline-flex items-center justify-center gap-2 rounded-md px-5 text-sm font-medium text-white transition-opacity disabled:opacity-50" style={{ background: WA_GREEN, minHeight: 42 }}>
                            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}{t("uwa.send.submit")}
                        </button>
                    </div>
                </div>
            </WaDialog>
            <WhatsAppQRModal open={qrOpen} onClose={() => setQrOpen(false)} />
        </>
    );
}
